import * as React from "react";
import {
  Button,
  Stack,
  Box,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import { useAddInvoiceMutation } from "../../services/invoice.service";
import { Invoice } from "../../types/invoice";
import { toast } from "react-toastify";
import { BaseModal, StyledTextField, SelectBase, SelectOption } from "../../components/global";

interface TopupModalFormProps {
  open: boolean;
  onClose: () => void;
}

const TopupModalForm = ({ open, onClose }: TopupModalFormProps) => {
  const [add, { isLoading: adding }] = useAddInvoiceMutation();

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const currencyOptions: SelectOption[] = [
    { value: "usd", label: "USD" },
    // { value: "eur", label: "EUR" },
  ];

  const onSubmit = async (payload: any) => {
    try {
      const invoice: Invoice = await add(payload).unwrap();

      if (invoice) {
        // 👇️ redirect to external URL
        window.location.replace(invoice.hosted_invoice_url);
      }

      // console.log(JSON.stringify(payload));
    } catch (err: any) {
      toast.error(err);
    }
  };

  React.useEffect(() => {
    if (!open) return;

    setValue("total", 100);
    setValue("currency", "usd");

    clearErrors();
  }, [open, setValue, clearErrors]);

  const footer = (
    <>
      <Button onClick={onClose} disabled={adding}>
        Cancel
      </Button>
      <LoadingButton
        type="submit"
        variant="contained"
        color="success"
        loading={adding}
        form="topup-form"
      >
        Submit
      </LoadingButton>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Top Up Balance"
      footer={footer}
      maxWidth="sm"
      useGlassCard={true}
    >
      <Box 
        component="form" 
        id="topup-form" 
        onSubmit={handleSubmit(onSubmit)} 
        sx={{ 
          p: 3,
          "@media (orientation: landscape) and (max-width: 900px)": {
            p: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Stack 
          spacing={3}
          sx={{
            "@media (orientation: landscape) and (max-width: 900px)": {
              "& > * + *": {
                mt: { xs: 2, sm: 2.5 },
              },
            },
          }}
        >
          <Controller
            name="currency"
            control={control}
            rules={{ required: "Currency is required" }}
            render={({ field: { onChange, value } }) => {
              const selected = currencyOptions.find((it) => it.value === value) || currencyOptions[0] || null;

              return (
                <SelectBase
                  label="Currency"
                  options={currencyOptions}
                  value={selected}
                  onChange={(newValue) => {
                    const singleValue = Array.isArray(newValue) ? newValue[0] : newValue;
                    onChange(singleValue?.value as string | undefined);
                  }}
                  error={!!errors.currency}
                  isSearchable={false}
                />
              );
            }}
          />
          <Controller
            name="total"
            control={control}
            rules={{
              required: "Amount is required",
              min: { value: 100, message: "Minimum amount is $100" }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <StyledTextField
                label="Amount"
                type="number"
                value={value || ""}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={(e) => {
                  e.target.select();
                }}
                error={!!errors.total}
                helperText={errors.total?.message as string || "Minimum amount: $100"}
                inputProps={{ min: 100, step: 1 }}
              />
            )}
          />
        </Stack>
      </Box>
    </BaseModal>
  );
};
export default TopupModalForm;
